import { omit } from 'lodash';
import { messageError } from '../../components/message';
import { withAPIAccount } from '../../lib/http/api';
import { APIError, parsedResponse } from '../../lib/http/http';
import { AccountType, IAPIresponse } from '../../model';
import {
  IAPIControlAccountItem,
  IAPIControlAccountBody,
  IAPICreateAccountRoleBody,
  IAPIResetPasswordBody,
  ICreateSupportAccountBody,
  IAPISupportAccountItem,
} from '../../model/api-modules';
import { ADMIN_PREFIX } from '../config';

// 管控账号-获取列表
export const getAdminAccountList = () =>
  withAPIAccount(AccountType.auto, async (api) => {
    try {
      const url = `${ADMIN_PREFIX}/control-account`;
      const response = await api.commonRequest('GET', url);
      const res = await parsedResponse<ReadonlyArray<IAPIControlAccountItem>>(
        response,
        'data'
      );
      return res;
    } catch (error) {
      return [];
    }
  });

// 管控账号-退出管控用户
export const logoutAdminAccount = (id: number) =>
  withAPIAccount(AccountType.auto, async (api) => {
    try {
      const url = `${ADMIN_PREFIX}/control-account/${id}`;
      const response = await api.commonRequest('GET', url);
      const res = await parsedResponse<IAPIresponse>(response, 'code', true);
      return res;
    } catch (error) {
      return [];
    }
  });

// 管控账号-创建临时密码
export const createPassword = (body: IAPIControlAccountBody) =>
  withAPIAccount(AccountType.auto, async (api) => {
    try {
      const url = `${ADMIN_PREFIX}/control-account`;
      const response = await api.commonRequest('POST', url, body);
      const res = await parsedResponse<IAPIresponse>(response, 'code');
      return res;
    } catch (error: any) {
      return error?.apiError;
    }
  });
// 管控账号-赋予权限
export const createAccountRole = (body: IAPICreateAccountRoleBody) =>
  withAPIAccount(AccountType.auto, async (api) => {
    try {
      const url = `${ADMIN_PREFIX}/control-account/grant-auth`;
      const response = await api.commonRequest('POST', url, body);
      const res = await parsedResponse<IAPIresponse>(response, 'code');
      return res;
    } catch (error: any) {
      return error?.apiError;
    }
  });
// 管控账号-刷新管控账号状态
export const refreshAccountStatus = () =>
  withAPIAccount(AccountType.auto, async (api) => {
    try {
      const url = `${ADMIN_PREFIX}/control-account/refresh-status`;
      const response = await api.commonRequest('PUT', url);
      const res = await parsedResponse<IAPIresponse>(response, 'code');
      return res;
    } catch (error: any) {
      return error?.apiError;
    }
  });

// 重置密码
export const resetPassword = (body: IAPIResetPasswordBody) =>
  withAPIAccount(AccountType.auto, async (api) => {
    try {
      const url = `${ADMIN_PREFIX}/user/changed-password`;
      const response = await api.commonRequest('PUT', url, body);
      const res = await parsedResponse<IAPIresponse>(response, 'code');
      return res;
    } catch (error: any) {
      return error?.apiError;
    }
  });

export const getSupportAccounts = () =>
  withAPIAccount(AccountType.auto, async (api) => {
    try {
      const url = `${ADMIN_PREFIX}/support-account-request`;
      const response = await api.commonRequest('GET', url);
      const res = await parsedResponse<{
        list: readonly IAPISupportAccountItem[];
      }>(response, 'data');
      return res.list;
    } catch (error: any) {
      return [];
    }
  });

export const createSupportAccount = (body: ICreateSupportAccountBody) =>
  withAPIAccount(AccountType.auto, async (api) => {
    try {
      const url = `${ADMIN_PREFIX}/support-account-request`;
      const response = await api.commonRequest('POST', url, body);
      await parsedResponse<IAPIresponse>(response, 'data');
      return true;
    } catch (error: any) {
      if (error instanceof APIError) {
        messageError(error?.apiError?.message);
      }
      return false;
    }
  });

export const approveSupportAccount = (id: number, portalURL: string) =>
  withAPIAccount(AccountType.auto, async (api) => {
    try {
      const url = `${ADMIN_PREFIX}/support-account-request/approved/${id}?portalURL=${portalURL}`;
      const response = await api.commonRequest('PUT', url);
      await parsedResponse<IAPIresponse>(response, 'data');
      return true;
    } catch (error: any) {
      if (error instanceof APIError) {
        messageError(error?.apiError?.message);
      }
      return false;
    }
  });

export const rejectSupportAccount = (id: number, RejectionReason: string) =>
  withAPIAccount(AccountType.auto, async (api) => {
    try {
      const url = `${ADMIN_PREFIX}/support-account-request/rejected/${id}`;
      const response = await api.commonRequest('PUT', url, { RejectionReason });
      await parsedResponse<IAPIresponse>(response, 'data');
      return true;
    } catch (error: any) {
      if (error instanceof APIError) {
        messageError(error?.apiError?.message);
      }
      return false;
    }
  });

export const recycleSupportAccount = (id: number) =>
  withAPIAccount(AccountType.auto, async (api) => {
    try {
      const url = `${ADMIN_PREFIX}/support-account-request/admin-recycle/${id}`;
      const response = await api.commonRequest('PUT', url);
      await parsedResponse<IAPIresponse>(response, 'data');
      return true;
    } catch (error: any) {
      if (error instanceof APIError) {
        messageError(error?.apiError?.message);
      }
      return false;
    }
  });

export const userReleaseSupportAccount = (id: number) =>
  withAPIAccount(AccountType.auto, async (api) => {
    try {
      const url = `${ADMIN_PREFIX}/support-account-request/user-active-release/${id}`;
      const response = await api.commonRequest('PUT', url);
      await parsedResponse<IAPIresponse>(response, 'data');
      return true;
    } catch (error: any) {
      if (error instanceof APIError) {
        messageError(error?.apiError?.message);
      }
      return false;
    }
  });

export const getAuditLog = (query: {
  envGuid?: string;
  envID?: number;
  game_id?: string;
  from_timestamp: number;
  to_timestamp: number;
  operator?: string;
  g6DomainURL: string;
}) =>
  withAPIAccount(AccountType.auto, async (api) => {
    try {
      const url = `${ADMIN_PREFIX}/ingame-console/proxy/env/v1/api/ingame/auditlogs/query?g6DomainURL=${query.g6DomainURL}`;
      const response = await api.commonRequest('GET', url, {
        ...omit(query, ['game_id']),
        query_type: 1,
        curr_page: 1,
        page_size: 1000,
        gameID: query.game_id,
        env_id: query.envGuid,
      });
      const res = await parsedResponse<Record<string, any>[]>(
        response,
        'result'
      );
      return res;
    } catch (error: any) {
      if (error instanceof APIError) {
        return error;
      }
      return [];
    }
  });
